import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as api from '../services/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toggleLang = () => { const next = i18n.language === 'pl' ? 'en' : 'pl'; i18n.changeLanguage(next); localStorage.setItem('swibi_language', next); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError(t('profile.passwordMinLength')); return; }
    if (password !== confirm) { setError(t('profile.passwordMismatch')); return; }
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.error || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const ic = "w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-white/5 rounded-full" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-white/5 rounded-full" />
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-blue-400/10 rounded-full blur-2xl" />
      </div>

      <div className="absolute top-4 right-4 z-20">
        <button onClick={toggleLang} className="text-sm text-blue-200 hover:text-white hover:bg-white/10 px-3 py-1.5 rounded-lg transition">
          {i18n.language === 'pl' ? 'English' : 'Polski'}
        </button>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="px-8 pt-8 pb-4 text-center border-b border-gray-100">
            <Link to="/">
              <img src="/logo_full.png" alt="SWIBI" className="h-10 mx-auto" />
            </Link>
          </div>

          <div className="p-8">
            <h1 className="text-lg font-bold text-gray-800 text-center mb-1">{t('auth.resetTitle')}</h1>
            <p className="text-gray-400 text-sm text-center mb-6">{t('auth.resetSubtitle')}</p>

            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle size={16} className="flex-shrink-0" />
                {error}
              </div>
            )}

            {!token ? (
              <div className="text-center space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {t('auth.invalidOrExpiredResetToken')}
                </div>
                <Link to="/forgot-password" className="inline-block text-blue-600 hover:text-blue-700 text-sm font-medium">
                  {t('auth.forgotTitle')}
                </Link>
              </div>
            ) : done ? (
              <div className="text-center space-y-4">
                <div className="flex flex-col items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  <CheckCircle size={28} className="text-green-500" />
                  {t('auth.passwordResetSuccess')}
                </div>
                <Link to="/login" className="inline-block text-blue-600 hover:text-blue-700 text-sm font-medium">
                  {t('auth.mfaBackToLogin')}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.newPasswordLabel')}</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    className={ic} placeholder="••••••••" required autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirmPasswordLabel')}</label>
                  <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                    className={ic} placeholder="••••••••" required />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50 transition shadow-lg shadow-blue-600/25 flex items-center justify-center gap-2">
                  <Lock size={16} /> {loading ? t('auth.loggingIn') : t('auth.resetSubmit')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
